import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Button,
  Autocomplete,
  ListItem,
  ListItemText,
  Alert,
  Grid,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import BackdropComponent from "components/Spinner/Spinner";
import FieldLabel from "components/LearnPoint/DTR/FieldLabel";
import { SnackBarStoreAction } from "Store/SnackBar";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import {
  InsertDTRDistro,
  UpdateDTRDistro,
  getAllDTRDistro,
} from "services/learnPointUserServices";
import { styled } from "@mui/material/styles";

const findInList = (list, key, searchItem) =>
  list?.find((item) => item[key]?.toLowerCase() === searchItem?.toLowerCase());

// ASTT-7319 follow-up: MUI's default Autocomplete filtering only matches
// against getOptionLabel(option)'s return value, which is DistroName
// alone below -- confirmed via direct feedback that typing an email
// address never matched anything, even when that address genuinely is
// one of the distro's members. This custom filter checks DistroEmail
// (the semicolon-separated member list) too, so a search can match
// either the distro's own name or any individual address inside it.
const distroFilterOptions = createFilterOptions({
  stringify: (option) => `${option?.DistroName ?? ""} ${option?.DistroEmail ?? ""}`,
});

const ttecEmailPattern =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(ttec\.com|ttecdigital\.com|percepta\.com)(;[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(ttec\.com|ttecdigital\.com|percepta\.com))*$/i;
const validateOccurance = (sourceName, currentName) => {
  const sourceNameWithOutCount = sourceName?.split("-")[0];
  const currentNameWithOutCount = currentName?.split("-")[0];
  return sourceNameWithOutCount === currentNameWithOutCount;
};

const noOfDistroNameOccuranceCount = (list, key, distroName) => {
  const filterByName = list.filter((item) =>
    validateOccurance(item[key]?.toLowerCase(), distroName?.toLowerCase()),
  );
  return filterByName?.length;
};

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "6px 20px",

  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));


export default function DtrEmailDistro(props) {
  const { open, onClose, isUpdate, disabled, updateEmailDistro, distroId } =
    props;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // ASTT-7319 follow-up: null, was an empty-but-real-shaped object
  // ({LP_DTRDistroId: 0, DistroName: "", DistroEmail: ""}). Confirmed via
  // a genuine, reproducible MUI console warning ("None of the options
  // match with ...") -- an object with LP_DTRDistroId: 0 can never match
  // a real option (0 is a "nothing selected" sentinel, not a real
  // database id), so isOptionEqualToValue was permanently failing to
  // find a match every time nothing was selected yet. null is what MUI's
  // Autocomplete actually expects for "no selection" and is handled
  // without any equality check at all, since it's checked for directly.
  // Every direct (non-optional-chained) property access on this value
  // elsewhere in this file is confirmed confined to the "update an
  // already-selected distro" flow, so this is safe.
  const initialEmailDistroValue = null;
  const [emailDistroValue, setEmailDistroValue] = useState(
    initialEmailDistroValue,
  );
  const [isDistroNameExists, setIsDistroNameExists] = useState(false);
  const [newDuplicateDistroName, setNewDuplicateDistroName] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (isUpdate && open) {
      setName(emailDistroValue?.DistroName);
      setEmail(emailDistroValue?.DistroEmail);
      distroNameValidation(emailDistroValue?.DistroName);
    }
  }, [isUpdate, open]);

  useEffect(() => {
    if (distroId > 0) {
      const distroValue = items.find(
        (item) => item.LP_DTRDistroId === distroId,
      );
      if (distroValue) {
        setEmailDistroValue(distroValue);
        updateEmailDistro(distroValue);
      }
    } else {
      setEmailDistroValue(initialEmailDistroValue);
      updateEmailDistro(initialEmailDistroValue);
    }
  }, [distroId, items]);

  useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setError({});
      setIsDistroNameExists(false);
    }
  }, [open]);

  const Snackhandler = (open, message, severity) => {
    const obj = {
      open: open,
      message: message,
      severity: severity,
    };

    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const validate = () => {
    let newErrors = {};
    if (!name) newErrors.name = "Name is Required";
    if (!email) {
      newErrors.email = "Email is Required";
    } else if (!ttecEmailPattern.test(email.trim())) {
      newErrors.email = "Email is Invalid";
    }
    return newErrors;
  };

  const saveValidations = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      return false;
    }
    return true;
  };
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        await fetchDTRDistro();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch DTR distro:", error);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchDTRDistro = async () => {
    setLoading(true);
    const response = await getAllDTRDistro();
    if (response?.message === "Success") {
      // ASTT-7319 follow-up: defensive de-dup by LP_DTRDistroId, the
      // one genuinely unique field here -- confirmed via console
      // warnings that this list can contain multiple rows sharing the
      // same DistroName (which the renderOption key fix above already
      // handles), but de-duping the source list itself is a safety net
      // in case the backend ever returns a truly duplicated row (same
      // id twice), which no key strategy alone could paper over.
      const seenIds = new Set();
      const dedupedItems = (response?.data ?? []).filter((item) => {
        if (seenIds.has(item.LP_DTRDistroId)) return false;
        seenIds.add(item.LP_DTRDistroId);
        return true;
      });
      setItems(dedupedItems);
    } else if (response?.statusCode === 404) {
      // ASTT-7319 follow-up: was response?.message === 404 -- comparing a
      // string field to a number, which can never be true, so this
      // branch was dead code; every 404 silently fell through to the
      // generic "Internal error occured" message below instead.
      Snackhandler(true, "No  Details found for the DTR Distro.", "info");
    } else {
      Snackhandler(true, "Internal error occured", "error");
    }
    setLoading(false);
  };

  const saveDTRDistro = async (distroName, distroEmail) => {
    setLoading(true);
    try {
      const result = await InsertDTRDistro(
        distroName,
        distroEmail,
        localStorage.getItem("User"),
      );

      if (result) {
        const newDistroId = result?.data[0]?.DistroId;
        const newItems = [
          ...items,
          {
            LP_DTRDistroId: newDistroId,
            DistroName: distroName,
            DistroEmail: distroEmail,
          },
        ];
        setItems(newItems);
        setEmailDistroValue({
          LP_DTRDistroId: newDistroId,
          DistroName: distroName,
          DistroEmail: distroEmail,
        });
        updateEmailDistro({
          LP_DTRDistroId: newDistroId,
          DistroName: distroName,
          DistroEmail: distroEmail,
        });
        Snackhandler(true, "Distro saved successfully.", "success");
        onClose();
      }
    } catch (error) {
      Snackhandler(true, "Error occued while saving Distro", "error");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!saveValidations()) {
      return;
    }
    await saveDTRDistro(name, email);
  };

  const handleEmailDistroChange = (event, value) => {
    if (value) {
      setEmailDistroValue(value);
      updateEmailDistro(value);
    } else {
      setEmailDistroValue(initialEmailDistroValue);
      updateEmailDistro(initialEmailDistroValue);
    }
  };

  const distroNameValidation = (DistroName) => {
    if (findInList(items, "DistroName", DistroName) !== undefined) {
      prepareNewDistroName(DistroName);
      setIsDistroNameExists(true);
    } else if (isDistroNameExists) {
      setIsDistroNameExists(false);
    }
  };

  const replaceDistroEmail = () => {
    const replacedList = items.map((item) => {
      if (item?.DistroName === name) {
        return {
          // ASTT-7319 follow-up: optional chaining added -- isDistroNameExists
          // (which is what actually controls whether "Save and Replace" even
          // renders) can become true purely from typing a name here that
          // happens to match an existing distro, via distroNameValidation --
          // this can happen even when the dialog was opened as "Add New"
          // (isUpdate=false) with nothing ever selected in the main
          // Autocomplete, meaning emailDistroValue is still null at this
          // point. A direct access here crashed in exactly that case,
          // repeating on every such click across both Training Notes and
          // Hand-off Report (both use this same shared component).
          DistroId: emailDistroValue?.LP_DTRDistroId,
          DistroName: name,
          DistroEmail: email,
        };
      }
      return item;
    });
    return replacedList;
  };

  const handleSaveAndReplace = async () => {
    if (!saveValidations()) {
      return;
    }
    const payload = {
      DistroId: emailDistroValue?.LP_DTRDistroId,
      DistroName: name,
      DistroEmail: email,
      modifiedBy: localStorage.getItem("User"),
    };
    try {
      const result = await UpdateDTRDistro(payload);
      if (result) {
        const newItems = replaceDistroEmail();
        setItems(newItems);
        const newDistroValue = {
          LP_DTRDistroId: emailDistroValue?.LP_DTRDistroId,
          DistroName: name,
          DistroEmail: email,
        };
        setEmailDistroValue(newDistroValue);
        updateEmailDistro(newDistroValue);

        Snackhandler(true, "Distro Replaced successfully.", "success");
        onClose();
      }
    } catch (error) {
      Snackhandler(true, "Error occued while Replacing Distro", "error");
    }
  };

  const prepareNewDistroName = (DistroName) => {
    const occuranceCount = noOfDistroNameOccuranceCount(
      items,
      "DistroName",
      DistroName,
    );
    const distroNameWithOutCount = DistroName?.split("-")[0];
    setNewDuplicateDistroName(`${distroNameWithOutCount}-${occuranceCount}`);
  };

  const handleSaveAsNew = async () => {
    if (!saveValidations()) {
      return;
    }
    await saveDTRDistro(newDuplicateDistroName, email);
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <div>
        {/* ASTT-7319: was label="Email Distro" on the TextField below --
            confirmed via direct feedback that MUI's internal label
            (centered inside an empty/unfocused field, reading as
            placeholder text) still wasn't visible enough as an actual
            label. Removed entirely; FieldLabel above the control matches
            the style already established by RichTextEditorField.js. */}
        <FieldLabel>Email Distro</FieldLabel>
        <Autocomplete
          disablePortal
          options={items}
          value={emailDistroValue}
          getOptionLabel={(option) => option?.DistroName || ""}
          filterOptions={distroFilterOptions}
          isOptionEqualToValue={(option, value) =>
            option?.LP_DTRDistroId === value?.LP_DTRDistroId
          }
          onChange={(event, value) => handleEmailDistroChange(event, value)}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  // ASTT-7319 follow-up: was 32 (pill-style), matches
                  // RichTextEditorField.js's actual radius now, per
                  // direct request.
                  borderRadius: "4px",
                  border: "1px solid #d3d3d3",
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            // ASTT-7319 follow-up: key switched from props.key (MUI's own,
            // internally derived from getOptionLabel -- i.e. DistroName)
            // to option.LP_DTRDistroId directly. Confirmed via console
            // warnings ("two children with the same key") that this
            // database genuinely has multiple distro records sharing the
            // same DistroName (e.g. two separate "LP Distro" rows) --
            // MUI's label-derived key collides in exactly that case, even
            // though the underlying records are different rows with
            // different ids. A real, guaranteed-unique id doesn't have
            // this problem regardless of how many rows share a display
            // name.
            const { key, ...optionProps } = props;
            return (
              <ListItem
                key={option?.LP_DTRDistroId ?? key}
                title={option?.DistroEmail}
                {...optionProps}
              >
                <ListItemText
                  primary={option?.DistroName}
                  secondary={option?.DistroEmail}
                ></ListItemText>
              </ListItem>
            );
          }}
        />
      </div>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {isDistroNameExists ? "Add / Edit Distro" : "Add New Distro"}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Distro name"
            type="text"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              distroNameValidation(e.target.value);
            }}
            error={!!error.name}
            helperText={error.name}
          />
          <TextField
            margin="dense"
            label="Email"
            multiline
            maxRows={4}
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error.email}
            helperText={error.email}
          />
          {isDistroNameExists && (
            <Alert severity="info">
              <Grid container>
                {!isUpdate && (
                  <Grid item xs={12}>
                    Distro Name Already exists.
                  </Grid>
                )}
                <Grid item xs={12}>
                  Save as New will save as <b>{newDuplicateDistroName}</b>
                </Grid>
              </Grid>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <CustomOutlinedButton variant="outlined"  onClick={onClose}>
            CANCEL
          </CustomOutlinedButton>
          {isDistroNameExists ? (
            <>
              <CustomButton
                variant="contained"
                color="primary"
                onClick={handleSaveAndReplace}
              >
                SAVE AND REPLACE
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleSaveAsNew}
              >
                SAVE AS NEW
              </CustomButton>
            </>
          ) : (
            <CustomButton variant="contained"  onClick={handleSave}>
              SAVE
            </CustomButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

DtrEmailDistro.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateEmailDistro: PropTypes.func,
  // isUpdate is a genuine boolean (Add vs Add/Edit dialog); distroId is the numeric/string
  // LP_DTRDistroId — never a function.
  isUpdate: PropTypes.bool,
  disabled: PropTypes.bool,
  distroId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
